/**
 * Save Rule Policy
 *
 * Responsibilities:
 * - React when loading.saving transitions to true (set by save/submit handler)
 * - Read the rule from state (component should dispatch data/setRule with edited
 *   version before dispatching save/submit)
 * - Call backend.commands.SetRule with the rule data
 * - On success: show toast, navigate to /automation
 * - On failure: reset saving loading state, throw error for global handler
 *
 * No optimistic update — success navigates away; the list will refetch.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { toast } from '@components/common/Toast/Toast';
import { RuleDetailState } from '../state';
import { RuleDetailCommand } from '../commands';

export const createSaveRulePolicy = (
  backend: ClientLibraryType,
  navigate: ( path: string ) => void
): Policy<RuleDetailState, RuleDetailCommand> => ({
  name: 'rule-detail-save',

  shouldReact: ( prev, next ) =>
    !prev.loading.saving && next.loading.saving,

  onStateChange: async ({ next, ctx }) => {
    const { rule } = next;

    if ( !rule ) {
      ctx.dispatch(
        { type: 'automation/ruleDetail/loading/setSavingLoading', payload: { isLoading: false }},
        { source: 'api' }
      );
      return;
    }

    try {
      // The rule in state should contain the edited data.
      // The component is expected to dispatch data/setRule with the form output
      // before dispatching save/submit.
      const entityId = ( rule as any ).hierarchyEntityIds?.[0] || ( rule as any ).id;

      // Strip read-only fields that come from the readmodel/definition
      // but should not be sent to the SetRule command
      const {
        id,
        name,
        declaredInputs,
        ruleDefinition,
        accountId,
        hierarchy,
        possibleTriggeringEvents,
        ...ruleBody
      } = rule as any;

      await backend.commands.SetRule({
        id: entityId,
        body: {
          ...ruleBody,
          ruleId: id
        }
      });

      toast.success({
        title: 'Regla guardada',
        message: 'La regla se guardó correctamente'
      });

      navigate( '/automation' );
    } catch ( error ) {
      ctx.dispatch(
        { type: 'automation/ruleDetail/loading/setSavingLoading', payload: { isLoading: false }},
        { source: 'api' }
      );
      throw error;
    }
  }
});
